import { Vector3 } from "three";
import { StreamablePointCloud, StreamablePointCloudNode } from ".";
import { toast } from "../../../app";
import { t } from "../../../localization";
import { readFileBuffer } from "../file-system-loader";
import LocalScene from "../projections";
import { WorkerPool } from "../worker";
import { parseAttributes, PointAttributes } from "./point-attributes";

class DataView64 extends DataView {
  constructor(buffer) {
    super(buffer);
  }

  getUint64(position, littleEndian = false) {
    return Number(this.getBigUint64(position, littleEndian));
  }

  getInt64(position, littleEndian = false) {
    return Number(this.getBigInt64(position, littleEndian));
  }
}

/** Encompass style pointcloud node */
class PotreePointCloudNode extends StreamablePointCloudNode {
  public cloud: PotreePointCloud;
  public parent: PotreePointCloudNode;

  public nodeType = 0;
  public hierarchyByteOffset = 0;
  public hierarchyByteSize = 0;
  public byteOffset = 0;
  public byteSize = 0;

  private attemptNumber = 0;
  private maxAttempts = 3;

  constructor(cloud, parent, name, level, center, size) {
    super(name, level, center, size);

    this.cloud = cloud;
    this.parent = parent;
  }

  get toMeters() {
    return LocalScene.dataToMeters;
  }

  getChildOffset(index) {
    return {
      x: (index & 0b100) / 4,
      y: (index & 0b010) / 2,
      z: index & 0b001
    };
  }

  async loadHierarchy(){
    if (this.nodeType !== 2) return;

    const byteRange = {
      first: this.hierarchyByteOffset,
      last: this.hierarchyByteOffset + this.hierarchyByteSize
    };

    const buffer = await readFileBuffer(
      this.cloud.hierarchyPath, byteRange);

    this.parseHierarchy(buffer);
  }

  parseHierarchy(buffer) {
    const view = new DataView64(buffer);
    const bytesPerNode = 22;
    const numNodes = buffer.byteLength / bytesPerNode;

    let nodePos = 1;
    const nodes = new Array(numNodes);
    nodes[0] = this;

    for (let i = 0; i < numNodes; i++) {
      const current = nodes[i] as PotreePointCloudNode;

      const type = view.getUint8(i * bytesPerNode + 0);
      const childMask = view.getUint8(i * bytesPerNode + 1);
      const numPoints = view.getUint32(i * bytesPerNode + 2, true);
      const byteOffset = view.getInt64(i * bytesPerNode + 6, true);
      const byteSize = view.getInt64(i * bytesPerNode + 14, true);

      if (current.nodeType === 2) {
        current.byteOffset = byteOffset;
        current.byteSize = byteSize;
        current.numPoints = numPoints;
      } else if(type === 2) {
        current.hierarchyByteOffset = byteOffset;
        current.hierarchyByteSize = byteSize;
        current.numPoints = numPoints;
      } else {
        current.byteOffset = byteOffset;
        current.byteSize = byteSize;
        current.numPoints = numPoints;
      }

      if (current.byteSize === 0) {
        current.numPoints = 0;
      }

      current.nodeType = type;
      if (current.nodeType === 2){
        continue;
      }

      for (let childIndex = 0; childIndex < 8; childIndex++){
        let childExists = ((1 << childIndex) & childMask) !== 0;
        if (!childExists) continue;

        const child = current.getChildNode(childIndex);
        current.addChildNode(child);

        nodes[nodePos] = child;
        nodePos++;
      }
    }
  }

  async getBinaryData() {
    const byteRange = {
      first: this.byteOffset,
      last: this.byteOffset + this.byteSize
    };

    let minCorner = new Vector3()
      .add(this.nodeCenter)
      .subScalar(this.size/2);

    let buffer;

    if (this.byteSize === 0) {
      buffer = new ArrayBuffer(0);
      console.warn(`Loaded node with 0 bytes: ${this.name}`);
    } else {
      buffer = await readFileBuffer(this.cloud.octreePath, byteRange);
    }

    this.sendToWorker({
      buffer: buffer,
      name: this.name,
      pointAttributes: this.cloud.pointAttributes,
      scale: this.cloud.xyzScale,
      min: this.cloud.xyzCenter,
      offset: this.cloud.xyzOffset,
      size: this.size,
      numPoints: this.numPoints,
      corner: minCorner,
      toMeters: this.toMeters
    });
  }

  sendToWorker(data) {
    let worker = this.workerPool.getWorker();

    worker.onmessage = event => {
      const data = event.data;

      // Convert raw buffer to float array
      data.positions = new Float32Array(data.attributeBuffers.positions.buffer);
      data.intensity = new Float32Array(data.attributeBuffers.intensity.buffer);
      data.classification = new Float32Array(data.attributeBuffers.classification.buffer);
      data.colors = new Float32Array(data.attributeBuffers.colors.buffer);

      this.getGeometry(data);

      this.workerPool.returnWorker(worker);
    };

    this.workerPool.sendMessage(worker, data, [data.buffer]);
  }

  getGeometry(data) {
    this.setGeometry(data);

    this.density = data.density;
    this.cloud.updateClassifications(data.classification);

    this.loaded = true;
    this.loading = false;
  }

  async load() {
    if ((this.loaded) || (this.loading)) return;

    this.loading = true;

    try {
      await this.loadHierarchy();
      await this.getBinaryData();
    } catch(err) {
      this.attemptNumber += 1;

      if (this.attemptNumber <= this.maxAttempts) {
        console.log(`Reload node ${this.name} (attempt #${this.attemptNumber})`);
        this.loaded = false;
        this.loading = false;
        return;
      }

      this.cloud.error = true;
      console.error(err);
    }
  }
}

/** Potree style pointcloud */
export class PotreePointCloud extends StreamablePointCloud {
  public xyzScale;
  public xyzOffset;
  public xyzCenter;
  public pointAttributes: PointAttributes;
  private version = "2.0";
  private encoding = "DEFAULT"

  constructor(parent) {
    super(parent);

    const workerURL = "workers/point-decoder-potree.js";
    const workerPath = `${process.env.PUBLIC_URL}/${workerURL}`;
    this.workerPool = new WorkerPool(workerPath);
  }

  get metadataPath() {
    return `${this.structure.path}/metadata.json`;
  }

  get octreePath() {
    return `${this.structure.path}/octree.bin`;
  }

  get hierarchyPath() {
    return `${this.structure.path}/hierarchy.bin`;
  }

  get toMeters() {
    return LocalScene.dataToMeters;
  }

  /** Decode cloud metadata. Cloud is in dataProjection units */
  decodeMetadata(data) {
    if (data.version !== this.version) {
      this.error = true;
      toast.error(t("toast.point_version_error", {
        version: data.version
      }));

      return;
    }

    if (data.encoding !== this.encoding) {
      this.error = true;
      toast.error(t("toast.point_encoding_error", {
        encoding: data.encoding
      }));

      return;
    }

    this.pointAttributes = parseAttributes(data.attributes);

    // Update maximum rgb value
    const colors = data.attributes.find(x => x.name === "rgb");
    const rgbMax = colors ? Math.max(...colors.max) : 0;
    this.updateColorLimits(rgbMax);

    // Update intensity bounds
    const intensity = data.attributes.find(x => x.name === "intensity");
    this.updateIntensityRange(intensity.min, intensity.max);

    const bboxMin = new Vector3(...data.boundingBox.min)
      .multiplyScalar(this.toMeters);

    const bboxMax = new Vector3(...data.boundingBox.max)
      .multiplyScalar(this.toMeters);

    const offset = new Vector3()
      .add(bboxMin)
      .add(bboxMax)
      .divideScalar(2.0);

    this.xyzScale = data.scale;
    this.xyzOffset = data.offset;
    this.xyzCenter = offset;
    this.spacing = data.spacing * this.toMeters;

    const position = data.attributes
      .find(x => x.name === "position");

    const xyzMin = new Vector3(...position.min)
      .multiplyScalar(this.toMeters);

    const xyzMax = new Vector3(...position.max)
      .multiplyScalar(this.toMeters);

    const tightBoundingBox = {
      min: new Vector3().add(xyzMin).sub(offset),
      max: new Vector3().add(xyzMax).sub(offset)
    };

    const nodeSize = bboxMax.x - bboxMin.x;
    const nodeCenter = new Vector3();

    this.shift = LocalScene.offsetToScene(offset);

    const rootNode = new PotreePointCloudNode(
      this, null, "r", 1, nodeCenter, nodeSize);

    rootNode.nodeType = 2;
    rootNode.hierarchyByteSize = data.hierarchy.firstChunkSize;

    this.root = rootNode;
    this.updateBoundingBox(tightBoundingBox);
    this.pointclouds.updateMinimapExtent();

    if (this.fromJSON) return;
    this.viewer.zoomToSceneExtent(this.tightBoundingBox, false);
  }
}